/**
 * The rulebase teaser on the workspace home.
 *
 * M1 put the full coverage cards here because there was nowhere else to put
 * them. M4 gives them a home of their own (`/workspace/:id/regelwerk`), so what
 * stays here is the part a reader needs while looking at their dossiers: how
 * many packs exist, how many are drafts, and which ones — each with the same
 * dashed indicative badge it carries on every other surface. The detail, the
 * source documents and the verification queue are one click away.
 *
 * Showing the draft names rather than only a count is deliberate: "3 Entwürfe"
 * is a number; "Zollikon ist ein Entwurf" is the thing that changes what
 * somebody does next.
 */
import { api } from '@/api/client'
import { DraftBadge } from '@/features/rulebase/DraftBadge'
import { isDraft, packIdOf, splitPackId } from '@/features/rulebase/rulebase'
import { useI18n } from '@/i18n'
import { Link, buildPath } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'
import type { GemeindePack } from '@/api/types'

export function GemeindeCoverage({ workspaceId }: { workspaceId: string }) {
  const { t } = useI18n()
  const packs = useAsync<GemeindePack[]>(
    (signal) => api.gemeinden(workspaceId, signal),
    [workspaceId],
  )

  const all = packs.data ?? []
  const drafts = all.filter(isDraft)

  return (
    <section aria-labelledby="gemeinden-heading" className="flex flex-col gap-3">
      <div>
        <h2 id="gemeinden-heading" className="font-display text-[21px] font-semibold">
          {t('start.gemeindenTitle')}
        </h2>
        <p className="mt-1 text-[13px] text-muted">{t('start.gemeindenLead')}</p>
      </div>

      {packs.loading ? <p className="text-[13px] text-muted">{t('common.loading')}</p> : null}
      {packs.error ? <p className="text-[13px] text-signal-red-text">{packs.error}</p> : null}
      {!packs.loading && !packs.error && all.length === 0 ? (
        <p className="text-[13px] text-muted">{t('start.gemeindenEmpty')}</p>
      ) : null}

      {all.length > 0 ? (
        <p className="text-[13px] text-muted-2" data-testid="coverage-summary">
          {t(drafts.length === 1 ? 'rulebase.summaryOne' : 'rulebase.summary', {
            total: all.length,
            verified: all.length - drafts.length,
            drafts: drafts.length,
          })}
        </p>
      ) : null}

      {drafts.length > 0 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-2" data-testid="coverage-drafts">
          {drafts.map((pack) => {
            const { kanton, gemeinde } = splitPackId(packIdOf(pack))
            return (
              <li key={packIdOf(pack)} className="flex items-center gap-2">
                <Link
                  to={buildPath('rulebasePack', { workspaceId, kanton, gemeinde })}
                  className="text-[13px] text-ink underline underline-offset-2"
                >
                  {pack.gemeinde}
                </Link>
                <DraftBadge />
              </li>
            )
          })}
        </ul>
      ) : null}

      <Link
        to={buildPath('rulebase', { workspaceId })}
        className="w-fit text-[13px] text-ink underline underline-offset-2"
      >
        {t('rulebase.title')}
      </Link>
    </section>
  )
}
